import { Layout } from '@abyss/web/ui/Layout';
import find from 'lodash/find';
import { useContext } from 'react';

import { PortalContext } from '../../context/PortalContext';
import { useCustomDisclaimerMessage } from '../../hooks/useCustomDisclaimerMessage';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ContentWrapper } from '../ContentWrapper';
import { DisclaimerContainer } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { isUmrMember } from '../Utils/memberUtils/memberUtils';
import { VisionDirectoryDataDisclaimer } from '../VisionCare/VisionDirectoryDataDisclaimer';
import {
  HealthGradesStyled,
  StyledMarkdown,
} from './DisclaimersContainer.styled';
import { HealthGradesDisclaimers } from './HealthGradesDisclaimers';
import { PremiumCareDisclaimer } from './PremiumCareDisclaimer';

interface Props {
  isGuestUser?: boolean;
  showPremiumCareDisclaimer?: boolean;
  isVisionCare?: boolean;
  ratingSection?: boolean;
  sectionType?: string;
  selectedChipValue?: string;
  search?: string;
  isVirtualVisit?: boolean;
}

export const ENIDisclaimersContainer = ({
  isGuestUser = false,
  showPremiumCareDisclaimer = false,
  isVirtualVisit = false,
  isVisionCare = false,
  ratingSection = false,
  sectionType,
  search,
}: Props) => {
  const lagoonData = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const isSmartChoice = useSmartChoice();
  const smartChoiceDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_DISCLAIMER,
  });
  const modalDisclaimerShort = find(lagoonData, {
    key: ConstantsLagoon.HEALTHGRADES.MODAL_DISCLAIMER_SHORT,
  });

  const modalDisclaimerFull = find(lagoonData, {
    key: ConstantsLagoon.HEALTHGRADES.MODAL_DISCLAIMER_FULL,
  });

  const disclaimerInPage = find(lagoonData, {
    key: ConstantsLagoon.HEALTHGRADES.DISCLAIMER_IN_PAGE,
  });
  const customDisclaimerInPageMessage = useCustomDisclaimerMessage(
    Constants.CUSTOM_DISCALIMER_BANNER_PAGE_NAME.HEALTH_GRADES_IN_PAGE
  );

  const guestGeneralDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.GUEST_GENERAL_DISCLAIMER,
  });

  const disclaimerInPageMessage =
    customDisclaimerInPageMessage ?? disclaimerInPage?.message ?? '';

  const visionDirectoryDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_CONTENT,
  });

  const visionDirectoryDisclaimerAddlContent = find(lagoonData, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_ADDL_CONTENT,
  });

  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const renderHealthGradesDisclaimer = () => {
    const isFacility = sectionType === Constants.FACILITY;
    const isOnlineProvider =
      search?.toLocaleLowerCase() ===
      Constants.ONLINE_PROVIDERS.toLocaleLowerCase();

    if (isFacility && !isOnlineProvider) return null;

    return (
      <HealthGradesDisclaimers
        disclaimerContent={modalDisclaimerShort?.message}
        modalContent={modalDisclaimerFull?.message}
      />
    );
  };

  const returnGuestDisclaimer = (disclaimer?: string) =>
    disclaimer || (isGuestUser ? guestGeneralDisclaimer?.message : '');

  const { portalData } = useContext(PortalContext);
  const umrMember = isUmrMember(portalData?.portalName);

  return (
    <DisclaimerContainer
      cssProps={{ ratingSection, umrMember, enableMapViewHideOption }}
    >
      <HealthGradesStyled
        cssProps={{ isWidget }}
        data-auto-testid="standard-disclaimer-content"
        data-testid="standard-disclaimer-content"
      >
        {!isVirtualVisit ? (
          <ContentWrapper isWidget={isWidget}>
            <Layout.Stack alignItems="left" space="0">
              <StyledMarkdown data-testid="standard-disclaimer">
                {`${returnGuestDisclaimer(disclaimerInPageMessage)} ${
                  isSmartChoice ? '\n\n' + smartChoiceDisclaimer?.message : ''
                }`}
              </StyledMarkdown>
              {isVisionCare && (
                <VisionDirectoryDataDisclaimer
                  visionDirectoryDisclaimerAddlContentMessage={
                    visionDirectoryDisclaimerAddlContent?.message
                  }
                  visionDirectoryDisclaimerMessage={
                    visionDirectoryDisclaimer?.message
                  }
                />
              )}
              {!isGuestUser ? (
                <>
                  <PremiumCareDisclaimer
                    isPDFView={false}
                    showPremiumCareDisclaimer={showPremiumCareDisclaimer}
                  />
                  {renderHealthGradesDisclaimer()}
                </>
              ) : null}
            </Layout.Stack>
          </ContentWrapper>
        ) : null}
      </HealthGradesStyled>
    </DisclaimerContainer>
  );
};
